/**
 * Resolving a scanned barcode to a product and the quantity that scan carries (ATL-1260).
 *
 * WHY THIS IS ITS OWN MODULE
 * --------------------------
 * It lived in `screens/NewSale/NewSaleScreen.js`, which two other screens imported it from —
 * `SupplyDetailScreen` and `TransferDetailScreen` both feed its weight straight into a stock
 * write. That made the one function three screens share untestable in isolation (importing the
 * screen pulls in the whole app), which is why the defect below survived a partial fix for
 * nine months. Same reasoning as `utils/cartAddition.js` and `utils/outOfStockGuard.ts`.
 *
 * THE BARCODE SCHEME
 * ------------------
 * `generateBarcode()` (utils/productValidation.js) mints a 6-digit INTERNAL code per product:
 * `20XXXX` for a piece good, `21XXXX` for a weighted ("весовой") one. That code is all a
 * product ever stores in `barcode`.
 *
 * A weighted product is not sold by that code, though — it is sold off a printed label, which
 * `PrintStickersScreen` composes as `code(6) + dayOfYear(3) + weight(3)` and renders as EAN-13,
 * so the scanner reads back 13 digits with a check digit appended. Hence the fixed offsets
 * below: the product is `substring(0, 6)` and the weight is `substring(9, 12)`, in hundredths
 * of a unit ('250' → 2.5 kg).
 */

/**
 * The quantity a scan carries, as a number that may be used as a cart / supply / transfer line
 * quantity. Never NaN, never zero.
 *
 * The weight segment exists ONLY on the 13-digit printed label. Scan a weighted product's bare
 * 6-digit internal code — which is what a merchant does when there is no printed sticker, and
 * what is shown on the product card — and `substring(9, 12)` is `''`, so `parseInt('')/100` is
 * `NaN`. The Dec-2024 guard here tested `weight === 0`, which catches the label's own '000'
 * "no weight" segment and nothing else: `NaN === 0` is false.
 *
 * A NaN weight is worse than a wrong one, because everything downstream reads it as "no answer"
 * rather than failing:
 *   - `needsEditor` (utils/cartAddition.js) opens the weight prompt on `weight === 1`;
 *     `NaN === 1` is false, so the prompt never opened and the line entered the cart with
 *     `quantity: NaN`;
 *   - NewSaleScreen's checkout filter keeps lines with `quantity > 0`, and `NaN > 0` is false,
 *     so that line was then dropped from the sale without a word. To the cashier the scan did
 *     nothing at all — the reported symptom, on every 21XXXX product in the catalog.
 *
 * So: anything that is not a usable quantity means "this barcode carries no weight", and the
 * answer to that is 1 — exactly what tapping the product tile passes, which routes the cashier
 * to the weight prompt instead of into a broken line. `!(weight > 0)` is deliberate over an
 * `isNaN` test: it covers NaN, 0 and a negative segment in one predicate, and it subsumes the
 * `=== 0` guard it replaces rather than sitting next to it.
 */
const usableWeight = (weight) => (weight > 0 ? weight : 1)

export const findProductByBarcode = (barcode, products) => {
  // Normalize: barcodes can arrive as numbers (Firestore-read mode) — string ops below
  // would throw on a numeric input (ATL-554)
  barcode = String(barcode ?? '')
  var productCode = 0
  var shortProductCode = 0
  var fullProductCode = parseInt(barcode)
  var weight = 1
  if (barcode.startsWith('20')) {
    productCode = parseInt(barcode.substring(0, 6))
  } else if (barcode.startsWith('21')) {
    productCode = parseInt(barcode.substring(0, 6))
    const weightCode = barcode.substring(9, 12)
    weight = usableWeight(parseInt(weightCode) / 100.0)
  } else {
    productCode = parseInt(barcode)
    shortProductCode = parseInt(barcode.substring(0, 6))
  }
  let item
  if (productCode !== 0 && !isNaN(productCode)) {
    item = products[productCode]
  } else {
    if (shortProductCode !== 0 && !isNaN(productCode)) {
      item = products[shortProductCode]
    }
  }
  if (!item) {
    item = products[fullProductCode]
  }
  if (!item) {
    // Raw-string fallback: the index stores barcodes verbatim, so leading-zero ('001234')
    // or alphanumeric supplier barcodes never match their parseInt form (ATL-554).
    // hasOwnProperty: never resolve inherited keys ('constructor') on plain-object dicts.
    item = Object.prototype.hasOwnProperty.call(products, barcode) ? products[barcode] : undefined
  }
  if (!item) {
    console.log('Product not found:', barcode)
  }
  return [item, weight]
}
